import React, { FunctionComponent } from 'react'
import { ImageSourcePropType, Platform, ViewStyle } from 'react-native'

import { isFunction } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { WellnessCheckInDomain } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/constants'
import { getWellnessCheckInTotalNumPages } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/utils'
import {
  WELLNESS_CHECK_IN_DOMAIN_TO_PAGE_NUMBER_OFFSET,
  WELLNESS_CHECK_IN_SOCIAL_CARE_PAGE_NUMBER,
} from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/wellnessCheckInMetadata'

import { Image } from '../../atoms/image/Image'
import { IS_WEB } from '../../constants'
import { useGetResponsiveAssets } from '../../hooks/useGetResponsiveAssets'
import { NavBar } from '../../molecules/navBar/NavBar'
import { WellnessCheckInDomainHeader } from '../../molecules/wellnessCheckInDomainHeader/WellnessCheckInDomainHeader'
import { ThemeType } from '../../utils/themes/ThemeProvider'

export interface WellnessCheckInHeaderProps {
  isQuestionnairePage?: boolean
  questionnairePageNumber?: number
  isResultsPage?: boolean
  socialCareEnabled?: boolean
  onExit?: () => void
  onBack?: () => void
  onLogoPress?: () => void
}

const PlaceholderView = styled.View({
  width: '40px',
  height: '40px',
})

export const WellnessCheckInHeader: FunctionComponent<WellnessCheckInHeaderProps> = ({
  isQuestionnairePage = false,
  questionnairePageNumber,
  isResultsPage = false,
  socialCareEnabled = false,
  onExit,
  onBack,
  onLogoPress,
}) => {
  const {
    colors,
    assets: { wellnessCheckInBackground },
  } = useTheme() as ThemeType

  const backgroundImage = useGetResponsiveAssets(wellnessCheckInBackground.assetNames)

  const showDomainInHeader =
    questionnairePageNumber &&
    questionnairePageNumber >= WELLNESS_CHECK_IN_DOMAIN_TO_PAGE_NUMBER_OFFSET &&
    questionnairePageNumber < WELLNESS_CHECK_IN_SOCIAL_CARE_PAGE_NUMBER

  const commonStyle = { zIndex: 1 }

  return (
    <>
      {!IS_WEB && (
        <Image
          source={backgroundImage as ImageSourcePropType}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          accessibilityIgnoresInvertColors
          contentFit={Platform.OS === 'android' ? 'cover' : 'none'}
        />
      )}
      <NavBar
        shouldShowCareNavigatorHeaderEntrypoint={false}
        isTransparent
        showProgressBar={isQuestionnairePage}
        showLogoMobileSized={!isQuestionnairePage}
        isFixed={!isResultsPage}
        showCloseButton={isFunction(onExit)}
        showBackButton={isFunction(onBack)}
        onBack={onBack}
        onExit={onExit}
        onLogoPress={onLogoPress}
        style={
          isResultsPage
            ? { ...commonStyle, position: 'absolute' }
            : {
                ...commonStyle,
                ...((IS_WEB && {
                  backgroundImage: `url(${backgroundImage})`,
                }) as ViewStyle),
              }
        }
        {...(isQuestionnairePage &&
          questionnairePageNumber && {
            progressBarProps: {
              progress: questionnairePageNumber / (getWellnessCheckInTotalNumPages(socialCareEnabled) + 1),
              backgroundColor: colors.backgroundPrimary,
              color: colors.textActive,
              width: -1,
              height: 6,
            },
          })}
        {...(!isResultsPage && {
          customRightComponent: showDomainInHeader ? (
            <WellnessCheckInDomainHeader
              wellnessCheckInDomain={
                Object.values(WellnessCheckInDomain)[
                  questionnairePageNumber - WELLNESS_CHECK_IN_DOMAIN_TO_PAGE_NUMBER_OFFSET
                ]
              }
            />
          ) : (
            // placeholder view that takes up same amount of space as WellnessCheckInDomainHeader
            // so the header is the same size throughout the Wellness Check In Questionnaire
            <PlaceholderView />
          ),
        })}
      />
    </>
  )
}
